import { ArgentinaProvinceAnalytics } from "@/components/analytics/ArgentinaProvinceAnalytics";
import { GoogleAnalyticsPageview } from "@/components/analytics/GoogleAnalyticsPageview";
import { PostHogPageview } from "@/components/analytics/PostHogPageview";
import { PostHogProvider } from "@/components/analytics/PostHogProvider";
import { PostHogTrackingVerification } from "@/components/analytics/PostHogTrackingVerification";
import { MetricoolTracker } from "@/components/analytics/MetricoolTracker";
import { ProvinceConversionTracker } from "@/components/analytics/ProvinceConversionTracker";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LanguageProvider } from "@/components/LanguageProvider";
import { LanguageUrlSync } from "@/components/LanguageUrlSync";
import { RegionProvider } from "@/components/RegionProvider";
import { SkipLink } from "@/components/SkipLink";
import { StructuredData } from "@/components/StructuredData";
import { RouteVideoStructuredData } from "@/components/VideoStructuredData";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CurrentTemplateProvider } from "@/contexts/CurrentTemplateContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useLayoutEffect, useState, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ClerkProvider } from "@clerk/clerk-react";
import { esES, enUS } from "@clerk/localizations";
import { dark } from "@clerk/themes";
import Argentina from "./pages/Argentina";
import InvitacionExclusivaArgentina from "./pages/InvitacionExclusivaArgentina";
import InvitacionArSuccess from "./pages/InvitacionArSuccess";
import InvitacionArError from "./pages/InvitacionArError";
import InvitacionArPending from "./pages/InvitacionArPending";
import PremiumInvite from "./pages/PremiumInvite";
// Code-split Mux conversion pages (also used by Index home)
const InvitacionExclusivaMux = lazy(() => import("./pages/InvitacionExclusivaMux"));
const DualCTAVideoLanding = lazy(() => import("./pages/DualCTAVideoLanding"));
import VideoVipLanding from "./pages/VideoVipLanding";
import InvitacionMiembro from "./pages/InvitacionMiembro";
import PremiumInviteSuccess from "./pages/PremiumInviteSuccess";
import MuchasGracias from "./pages/MuchasGracias";
import MembresiaPremiumVip from "./pages/MembresiaPremiumVip";
import TestNewTemplate from "./pages/TestNewTemplate";
import TestPremiumArgentina from "./pages/TestPremiumArgentina";
import TestPremiumInternacional from "./pages/TestPremiumInternacional";
import GeneradorInvitaciones from "./pages/admin/GeneradorInvitaciones";
import ArgenLinksAdmin from "./pages/admin/ArgenLinksAdmin";
import Artista from "./pages/Artista";
import Atleta from "./pages/Atleta";
import BeautyExpert from "./pages/BeautyExpert";
import Belleza from "./pages/Belleza";
import Biografia from "./pages/Biografia";
import BuenosAires from "./pages/BuenosAires";
import Contacto from "./pages/Contacto";
import Contenido from "./pages/Contenido";
import ContentCreator from "./pages/ContentCreator";
import CreadoraContenido from "./pages/CreadoraContenido";
import Creative from "./pages/Creative";
import Deportista from "./pages/Deportista";
import Desnuda from "./pages/Desnuda";
import DigitalCreator from "./pages/DigitalCreator";
import Entrenamiento from "./pages/Entrenamiento";
import Erome from "./pages/Erome";
import Exclusivo from "./pages/Exclusivo";
import ExclusivoArSuccess from "./pages/ExclusivoArSuccess";
import Fashion from "./pages/Fashion";
import FullscreenExclusiveAr from "./pages/FullscreenExclusiveAr";
import Filtrado from "./pages/Filtrados";
import Filtro from "./pages/Filtro";
import Fitness from "./pages/Fitness";
import Fotos from "./pages/Fotos";
import FotosDesnuda from "./pages/FotosDesnuda";
import FotosGratis from "./pages/FotosGratis";
import Galeria from "./pages/Galería";
import Gracias from "./pages/Gracias";
import GraciasPorTuCompra from "./pages/GraciasPorTuCompra";
import GraciasPorTuCompraLatam from "./pages/GraciasPorTuCompraLatam";
import Gratis from "./pages/Gratis";
import Imagenes from "./pages/Imagenes";
import Index from "./pages/Index";
import IndexMinimal from "./pages/IndexMinimal";
import Influencer from "./pages/Influencer";
import Instagram from "./pages/Instagram";
import Invitacion from "./pages/Invitacion";
import InvitacionGrupo from "./pages/InvitacionGrupo";
import Invite from "./pages/Invite";
import InviteGenerator from "./pages/InviteGenerator";
import Leaked from "./pages/Leaked";
import Lifestyle from "./pages/Lifestyle";
import MembresiaTelegram from "./pages/MembresiaTelegram";
import Modelo from "./pages/Modelo";
import NotFound from "./pages/NotFound";
import Nude from "./pages/Nude";
import Nutricion from "./pages/Nutricion";
import Onlyfans from "./pages/Onlyfans";
import Pack from "./pages/Pack";
import Personalidad from "./pages/Personalidad";
import Photography from "./pages/Photography";
import Pornhub from "./pages/Pornhub";
import Premium from "./pages/Premium";
import Privado from "./pages/Privado";
import Recetas from "./pages/Recetas";
import Pack1 from "./pages/Pack1";
import Pack2 from "./pages/Pack2";
import Pack3 from "./pages/Pack3";
// import RegaloLucas from "./pages/RegaloLucas"; // oculto mientras se trabaja
import CrearProducto from "./pages/admin/CrearProducto";
import SubscripcionesPlanes from "./pages/admin/SubscripcionesPlanes";
import EnvSetup from "./pages/admin/EnvSetup";
import GiftProduct from "./pages/GiftProduct";
import ProductPageSimple from "./pages/ProductPageSimple";
import DirectCheckout from "./pages/DirectCheckout";
import ProductReel from "./pages/ProductReel";
import ProductReelMockTest from "./pages/ProductReelMockTest";
import Reels from "./pages/Reels";
import ReelInviteViewer from "./pages/ReelInviteViewer";
import ProductPageMockUsd from "./pages/ProductPageMockUsd";
import ProductPageMockArs from "./pages/ProductPageMockArs";
import Rutina from "./pages/Rutina";
import Skincare from "./pages/Skincare";
import SocialMedia from "./pages/SocialMedia";
import TestMuxCountry from "./pages/TestMuxCountry";
import MuxVideoCountryTest from "./components/dev/MuxVideoCountryTest";
import TestCheckout from "./pages/TestCheckout";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutArChannelInvite from "./pages/CheckoutArChannelInvite";
import CheckoutChannelInviteRouter from "./pages/CheckoutChannelInviteRouter";
import PagarPage from "./pages/PagarPage";
import ChannelInviteLanding from "./pages/ChannelInviteLanding";
import FullscreenExclusive from "./pages/FullscreenExclusive";
import FullscreenSorpresa from "./pages/FullscreenSorpresa";
import Sorprendeme from "./pages/Sorprendeme";
import VipCustomPayment from "./pages/VipCustomPayment";
import TestExclusivo from "./pages/TestExclusivo";
import WhatsAppBroadcast from "./pages/WhatsAppBroadcast";
import WhatsAppBroadcastSuccess from "./pages/WhatsAppBroadcastSuccess";
import TestPage from "./pages/TestPage";
import { TestRunner } from "./pages/TestRunner";
import ProtectedTestRoute from "./components/ProtectedTestRoute";
import StripeValidation from "./pages/StripeValidation";
import ThankYou from "./pages/ThankYou";
import TikTok from "./pages/TikTok";
import Tiktoker from "./pages/Tiktoker";
import TravelInfluencer from "./pages/TravelInfluencer";
import Viajes from "./pages/Viajes";
import Video from "./pages/Video";
import VideosDesnuda from "./pages/VideosDesnuda";
import VideosGratis from "./pages/VideosGratis";
import Wellness from "./pages/Wellness";
import Xvideos from "./pages/Xvideos";
import YouTube from "./pages/YouTube";
import TerminosYCondiciones from "./pages/TerminosYCondiciones";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DarDeBaja from "./pages/DarDeBaja";
import Portfolio from "./pages/Portfolio";
import MediaKitIntro from "./pages/MediaKitIntro";
import MediaKitDesktop from "./pages/MediaKitDesktop";
import MediaKitPasswordGate from "./components/MediaKitPasswordGate";
import Audiovisual from "./pages/Audiovisual";
import PortfolioAdmin from "./pages/admin/PortfolioAdmin";
import AudiovisualAdmin from "./pages/admin/AudiovisualAdmin";
import BaseTemplateAdmin from "./pages/admin/BaseTemplateAdmin";
import TemplatesPorPaisPage from "./pages/admin/TemplatesPorPaisPage";
import CentroControlAdmin from "./pages/admin/CentroControlAdmin";
import WishlistAdmin from "./pages/admin/WishlistAdmin";
import CTAManager from "./pages/admin/CTAManager";
import WorkerFlagsAdmin from "./pages/admin/WorkerFlagsAdmin";
import SidebarConfigAdmin from "./pages/admin/SidebarConfigAdmin";
import ConfigHealthAdmin from "./pages/admin/ConfigHealthAdmin";
import IntegrationsAdmin from "./pages/admin/IntegrationsAdmin";
import RevenueAdmin from "./pages/admin/RevenueAdmin";
import PersonalFinanceAdmin from "./pages/admin/PersonalFinanceAdmin";
import FinanzasPersonalesAdmin from "./pages/admin/FinanzasPersonalesAdmin";
import InvestorKpiAdmin from "./pages/admin/InvestorKpiAdmin";
import CheckoutFunnelAdmin from "./pages/admin/CheckoutFunnelAdmin";
import RecoveryLeadsAdmin from "./pages/admin/RecoveryLeadsAdmin";
import LiveFunnelAdmin from "./pages/admin/LiveFunnelAdmin";
import IspReportAdmin from "./pages/admin/IspReportAdmin";
import KimiPatternsAdmin from "./pages/admin/KimiPatternsAdmin";
import TestBaseTemplate from "./pages/TestBaseTemplate";
import TestCTAButtons from "./pages/TestCTAButtons";
import AdminDashboard from "./pages/admin/AdminDashboard";
import PrivateReelsLayout from "./pages/admin/reels/PrivateReelsLayout";
import PrivateReelsHub from "./pages/admin/reels/PrivateReelsHub";
import PrivateReelsVideosPage from "./pages/admin/reels/PrivateReelsVideosPage";
import PrivateReelsAccessPage from "./pages/admin/reels/PrivateReelsAccessPage";
import PrivateReelsLinksPage from "./pages/admin/reels/PrivateReelsLinksPage";
import PrivateReelsSecretSharePage from "./pages/admin/reels/PrivateReelsSecretSharePage";
import PremiumInviteAdmin from "./pages/admin/PremiumInviteAdmin";
import MembershipApprovals from "./pages/admin/MembershipApprovals";
import BuyersAdmin from "./pages/admin/BuyersAdmin";
import StripeDisputesAdmin from "./pages/admin/StripeDisputesAdmin";
import ImageMetricsAdmin from "./pages/admin/ImageMetricsAdmin";
import Registro from "./pages/Registro";
import CompleteRegistration from "./pages/CompleteRegistration";
import FreeAccessReady from "./pages/FreeAccessReady";
import { FREE_JOIN_ROUTE } from "./lib/freeJoinFlow";
import PendingApproval from "./pages/PendingApproval";
import MiAcceso from "./pages/MiAcceso";
import MembershipAccessPortal from "./pages/MembershipAccessPortal";
import BestFriendInstagram from "./pages/BestFriendInstagram";
import BestFriendInstagramSuccess from "./pages/BestFriendInstagramSuccess";
import {
  MEMBERSHIP_ACCESS_PORTAL_PATH,
  MEMBERSHIP_ROUTE,
  MEMBERSHIP_ROUTE_EN_COMPLETE_PROFILE,
} from "@/lib/membershipAccessFlow";
import { ProtectedAdminRoute } from "./components/ProtectedAdminRoute";
import { AdminPreviewBanner } from "./components/AdminPreviewBanner";
import { GeoSimToolbar } from "./components/GeoSimToolbar";
import { VisitorPrivacyGate } from "./components/VisitorPrivacyGate";
import {
  getClerkPublishableKey,
  isLikelyClerkPublishableKey,
} from "@/lib/clerkPublishableKey";

const queryClient = new QueryClient();

/**
 * index.html incluye #seo-static-snapshot para crawlers sin JS. Con React activo el pie real es el de cada
 * plantilla (p. ej. BaseTemplateFooter); si no removemos el snapshot, hay dos footers duplicados.
 */
function RemoveSeoStaticSnapshot() {
  useLayoutEffect(() => {
    document.getElementById("seo-static-snapshot")?.remove();
  }, []);
  return null;
}

// Test routes (test-mux-video, test-mux-country, test-checkout, etc.) solo activas con VITE_ENABLE_TEST_ROUTES=true
const TEST_ROUTES_ENABLED = import.meta.env.VITE_ENABLE_TEST_ROUTES === "true";

// Clerk: VITE_ / NEXT_PUBLIC_ en cliente; build también puede mapear CLERK_PUBLISHABLE_KEY (ver vite.config.ts).
const CLERK_PUBLISHABLE_KEY = getClerkPublishableKey();

if (import.meta.env.DEV && CLERK_PUBLISHABLE_KEY && !isLikelyClerkPublishableKey(CLERK_PUBLISHABLE_KEY)) {
  console.warn(
    "[Clerk] La publishable key no parece válida (esperado pk_test_… o pk_live_…). Revisa variables en .env / Railway."
  );
}

/**
 * Clerk debe vivir **dentro** de BrowserRouter y recibir routerPush/routerReplace;
 * si no, useAuth puede quedar en isLoaded=false y RedirectToSignIn no muestra login (Clerk v5 + React Router).
 */
function AppClerkProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const afterAuthUrl = `${pathname}${search}` || "/admin";

  if (!CLERK_PUBLISHABLE_KEY) {
    return <>{children}</>;
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      routerPush={(to) => {
        void navigate(to);
      }}
      routerReplace={(to) => {
        void navigate(to, { replace: true });
      }}
      signInFallbackRedirectUrl={afterAuthUrl}
      signUpFallbackRedirectUrl={afterAuthUrl}
      localization={getClerkLocale()}
      appearance={{
        baseTheme: dark,
        variables: {
          colorPrimary: "hsl(var(--primary))",
          colorBackground: "hsl(240, 10%, 10%)",
          colorText: "hsl(240, 5%, 95%)",
          colorTextSecondary: "hsl(240, 5%, 72%)",
          colorInputBackground: "hsl(240, 8%, 16%)",
          colorInputText: "hsl(240, 5%, 95%)",
          colorNeutral: "hsl(240, 5%, 95%)",
          borderRadius: "var(--radius)",
        },
      }}
    >
      {children}
    </ClerkProvider>
  );
}

// Get Clerk locale based on browser language
function getClerkLocale(): typeof esES | typeof enUS {
  if (typeof window !== "undefined") {
    const browserLang = navigator.language || "";
    if (browserLang.startsWith("es")) return esES;
  }
  return enUS;
}

// Loading component while i18n initializes
function I18nLoadingWrapper({ children }: { children: React.ReactNode }) {
  const { i18n, t } = useTranslation();
  const [showContent, setShowContent] = useState(false);
  const [forceShow, setForceShow] = useState(false);

  useEffect(() => {
    // Force show after 1.2s if i18n stalls (was 3s — felt broken on AR 4G).
    const forceTimer = setTimeout(() => {
      if (!showContent) {
        console.warn("[I18n] Timeout reached, forcing content display");
        setForceShow(true);
        setShowContent(true);
      }
    }, 1200);

    return () => clearTimeout(forceTimer);
  }, [showContent]);

  useEffect(() => {
    // Show content as soon as i18n is ready — no artificial 100ms delay.
    if (i18n.isInitialized && i18n.language) {
      setShowContent(true);
    }
  }, [i18n.isInitialized, i18n.language]);

  if (!showContent && !forceShow) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black">
        <Loader2 className="w-8 h-8 text-white/40 animate-spin mb-3" aria-hidden />
        <p className="text-xs text-white/50 tracking-wide">Cargando…</p>
      </div>
    );
  }

  return <>{children}</>;
}

const App = () => {
  if (!CLERK_PUBLISHABLE_KEY) {
    console.error(
      "[Clerk] Falta publishable key. Define VITE_CLERK_PUBLISHABLE_KEY o NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY (o CLERK_PUBLISHABLE_KEY en el build) y redeploy. Sin eso, /admin no puede usar Clerk."
    );
  }

  const appContent = (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <PostHogProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <RegionProvider>
              <LanguageProvider>
                <BrowserRouter>
                  <AppClerkProvider>
                  <RemoveSeoStaticSnapshot />
                  <CurrentTemplateProvider>
                    <I18nLoadingWrapper>
                      <LanguageUrlSync />
                      <SkipLink />
                      <ArgentinaProvinceAnalytics />
                      <ProvinceConversionTracker />
                      <StructuredData />
                      <RouteVideoStructuredData />
                      <AdminPreviewBanner />
                      <GeoSimToolbar />
                      <VisitorPrivacyGate />
                      <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/minimal" element={<IndexMinimal />} />
                    {/* /test — réplica de la home Argentina (BaseTemplate) con CTA buttons de prueba */}
                    <Route path="/test" element={<TestCTAButtons />} />
                    {/* Rutas de test desactivadas por defecto; solo con VITE_ENABLE_TEST_ROUTES=true */}
                    {TEST_ROUTES_ENABLED ? (
                      <>
                        <Route path="/test-runner" element={<ProtectedTestRoute><TestRunner /></ProtectedTestRoute>} />
                        <Route path="/test-mux-country" element={<ProtectedTestRoute><TestMuxCountry /></ProtectedTestRoute>} />
                        <Route path="/test-mux-video" element={<ProtectedTestRoute><MuxVideoCountryTest /></ProtectedTestRoute>} />
                        <Route path="/test-checkout" element={<ProtectedTestRoute><TestCheckout /></ProtectedTestRoute>} />
                        <Route path="/validate-stripe" element={<ProtectedTestRoute><StripeValidation /></ProtectedTestRoute>} />
                      </>
                    ) : (
                      <>
                        <Route path="/test-runner" element={<Navigate to="/" replace />} />
                        <Route path="/test-mux-country" element={<Navigate to="/" replace />} />
                        <Route path="/test-mux-video" element={<Navigate to="/" replace />} />
                        <Route path="/test-checkout" element={<Navigate to="/" replace />} />
                        <Route path="/validate-stripe" element={<Navigate to="/" replace />} />
                      </>
                    )}
                    {/* Admin - Layout con sidebar y rutas anidadas */}
                    <Route path="/admin/" element={<Navigate to="/admin" replace />} />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedAdminRoute title="Panel de Administración">
                          <AdminLayout />
                        </ProtectedAdminRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route
                        path="revenue"
                        element={
                          <ProtectedAdminRoute title="Revenue">
                            <RevenueAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="finanzas-personales"
                        element={
                          <ProtectedAdminRoute
                            title="Finanzas personales"
                            requireRole="super_admin"
                          >
                            <FinanzasPersonalesAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      {/* Alias legacy thin page → same surface */}
                      <Route
                        path="personal-finance"
                        element={
                          <ProtectedAdminRoute
                            title="Finanzas personales"
                            requireRole="super_admin"
                          >
                            <FinanzasPersonalesAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="investor-kpi"
                        element={
                          <ProtectedAdminRoute title="KPIs Investors" requireRole="super_admin">
                            <InvestorKpiAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route path="centro-de-control" element={<CentroControlAdmin />} />
                      <Route path="site-settings" element={<BaseTemplateAdmin />} />
                      {/* DualCTA home: Mux playback + Stripe VIP por país.
                          Canonical + aliases (legacy + dual-cta shortcuts). */}
                      <Route path="country-templates" element={<TemplatesPorPaisPage />} />
                      <Route path="templates-por-pais" element={<TemplatesPorPaisPage />} />
                      <Route path="dual-cta" element={<TemplatesPorPaisPage />} />
                      <Route path="dual-cta-home" element={<TemplatesPorPaisPage />} />
                      <Route
                        path="invitaciones"
                        element={
                          <ProtectedAdminRoute title="Generador de invitaciones" requireRole="super_admin">
                            <GeneradorInvitaciones />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route path="invitaciones/" element={<Navigate to="/admin/invitaciones" replace />} />
                      <Route path="argen-links" element={<ArgenLinksAdmin />} />
                      <Route path="portfolio" element={<PortfolioAdmin />} />
                      <Route path="audiovisual" element={<AudiovisualAdmin />} />
                      <Route
                        path="reels"
                        element={
                          <ProtectedAdminRoute title="Reels privados">
                            <PrivateReelsLayout />
                          </ProtectedAdminRoute>
                        }
                      >
                        <Route index element={<PrivateReelsHub />} />
                        <Route path="videos" element={<PrivateReelsVideosPage />} />
                        <Route path="acceso" element={<PrivateReelsAccessPage />} />
                        <Route path="links" element={<PrivateReelsLinksPage />} />
                        <Route
                          path="secret-share"
                          element={<PrivateReelsSecretSharePage />}
                        />
                      </Route>
                      <Route path="wishlist-config" element={<WishlistAdmin />} />
                      <Route path="premium-invite-config" element={<PremiumInviteAdmin />} />
                      <Route path="memberships" element={<MembershipApprovals />} />
                      <Route
                        path="buyers"
                        element={
                          <ProtectedAdminRoute title="Compradores" requireRole="super_admin">
                            <BuyersAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route path="stripe-disputes" element={<StripeDisputesAdmin />} />
                      <Route path="image-metrics" element={<ImageMetricsAdmin />} />
                      <Route path="cta" element={<CTAManager />} />
                      <Route
                        path="worker-flags"
                        element={
                          <ProtectedAdminRoute title="Worker / Edge" requireRole="super_admin">
                            <WorkerFlagsAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="config-health"
                        element={
                          <ProtectedAdminRoute title="Config Health" requireRole="super_admin">
                            <ConfigHealthAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="integraciones"
                        element={
                          <ProtectedAdminRoute title="Integraciones" requireRole="super_admin">
                            <IntegrationsAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="checkout-funnel"
                        element={
                          <ProtectedAdminRoute title="Funnel de checkout" requireRole="super_admin">
                            <CheckoutFunnelAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="recovery-leads"
                        element={
                          <ProtectedAdminRoute title="Leads de recovery" requireRole="super_admin">
                            <RecoveryLeadsAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="live-funnel"
                        element={
                          <ProtectedAdminRoute title="Live funnel" requireRole="super_admin">
                            <LiveFunnelAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="isp-report"
                        element={
                          <ProtectedAdminRoute title="Proveedores de internet" requireRole="super_admin">
                            <IspReportAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="kimi-patterns"
                        element={
                          <ProtectedAdminRoute title="Kimi / offer engine" requireRole="super_admin">
                            <KimiPatternsAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="productos"
                        element={
                          <ProtectedAdminRoute title="Crear Producto">
                            <CrearProducto />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="suscripciones"
                        element={
                          <ProtectedAdminRoute title="Planes de Suscripción">
                            <SubscripcionesPlanes />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route
                        path="env-setup"
                        element={
                          <ProtectedAdminRoute title="Variables de entorno" requireRole="super_admin">
                            <EnvSetup />
                          </ProtectedAdminRoute>
                        }
                      />
                      <Route path="vercel-setup" element={<Navigate to="/admin/env-setup" replace />} />
                      <Route
                        path="sidebar-config"
                        element={
                          <ProtectedAdminRoute title="Visibilidad del sidebar" requireRole="super_admin">
                            <SidebarConfigAdmin />
                          </ProtectedAdminRoute>
                        }
                      />
                    </Route>
                    {/* Legacy redirects — mantienen compatibilidad con links externos */}
                    <Route path="/dashboard/users/actividad" element={<Navigate to="/admin/investor-kpi" replace />} />
                    <Route path="/dashboard/crear-producto" element={<Navigate to="/admin/productos" replace />} />
                    <Route path="/dashboard/subscripciones/planes" element={<Navigate to="/admin/suscripciones" replace />} />
                    <Route path="/dashboard/vercel-setup" element={<Navigate to="/admin/env-setup" replace />} />
                    <Route path="/dashboard/env-setup" element={<Navigate to="/admin/env-setup" replace />} />
                    <Route
                      path="/test-base-template"
                      element={
                        <ProtectedAdminRoute title="Vista previa BaseTemplate">
                          <TestBaseTemplate />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/test-cta-buttons"
                      element={
                        <ProtectedAdminRoute title="Test CTA Buttons por País">
                          <TestCTAButtons />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route path="/gift/:slug" element={<GiftProduct />} />
                    <Route path="/p/mock-usd" element={<ProductPageMockUsd />} />
                    <Route path="/p/mock-ars" element={<ProductPageMockArs />} />
                    <Route path="/p/:slug" element={<ProductPageSimple />} />
                    <Route path="/buy/:slug" element={<DirectCheckout />} />
                    <Route path="/reel/:slug" element={<ProductReel />} />
                    <Route path="/test/reel/:slug" element={<ProductReelMockTest />} />
                    <Route
                      path="/canal"
                      element={<ChannelInviteLanding />}
                    />
                    <Route
                      path="/channel"
                      element={<ChannelInviteLanding />}
                    />
                    <Route
                      path="/checkout-channel-invite-router"
                      element={<CheckoutChannelInviteRouter />}
                    />
                    <Route
                      path="/checkout-channel-invite"
                      element={<CheckoutPage />}
                    />
                    <Route
                      path="/checkout-ar-channel-invite"
                      element={<CheckoutArChannelInvite />}
                    />
                    <Route path="/pagar/:paymentIntentId" element={<PagarPage />} />
                    <Route path="/generator" element={<InviteGenerator />} />
                    <Route path="/invitacion" element={<PremiumInvite />} />
                    <Route
                      path="/invitacion-mejores-amigos"
                      element={<InvitacionGrupo />}
                    />
                    <Route
                      path="/grupo-privado"
                      element={<InvitacionGrupo />}
                    />
                    <Route path="/invite" element={<Invite />} />
                    <Route
                      path="/creadora-de-contenido"
                      element={<CreadoraContenido />}
                    />
                    <Route
                      path="/content-creator"
                      element={<ContentCreator />}
                    />
                    <Route path="/influencer" element={<Influencer />} />
                    <Route path="/fotos" element={<Fotos />} />
                    <Route path="/imagenes" element={<Imagenes />} />
                    {/* Private password gate — /reels; secret-share Mux — /reels/:token */}
                    <Route path="/reels" element={<Reels />} />
                    <Route path="/reels/:token" element={<ReelInviteViewer />} />
                    <Route path="/video" element={<Video />} />
                    <Route path="/lifestyle" element={<Lifestyle />} />
                    <Route path="/nutricion" element={<Nutricion />} />
                    <Route path="/belleza" element={<Belleza />} />
                    <Route path="/viajes" element={<Viajes />} />
                    <Route path="/tiktok" element={<TikTok />} />
                    <Route path="/instagram" element={<Instagram />} />
                    <Route path="/youtube" element={<YouTube />} />
                    <Route path="/erome" element={<Erome />} />
                    <Route path="/filtrados" element={<Filtrado />} />
                    <Route path="/filtro" element={<Filtro />} />
                    <Route path="/leaked" element={<Leaked />} />
                    <Route path="/onlyfans" element={<Onlyfans />} />
                    <Route path="/pack" element={<Pack />} />
                    <Route path="/pack1" element={<Pack1 />} />
                    <Route path="/pack2" element={<Pack2 />} />
                    <Route path="/pack3" element={<Pack3 />} />
                    <Route path="/desnuda" element={<Desnuda />} />
                    <Route path="/nude" element={<Nude />} />
                    <Route path="/premium" element={<Premium />} />
                    <Route path="/privado" element={<Privado />} />
                    <Route path="/contenido" element={<Contenido />} />
                    <Route path="/exclusivo" element={<Exclusivo />} />
                    <Route path="/gratis" element={<Gratis />} />
                    <Route path="/galeria" element={<Galeria />} />
                    <Route path="/fotos-desnuda" element={<FotosDesnuda />} />
                    <Route path="/videos-desnuda" element={<VideosDesnuda />} />
                    <Route path="/pornhub" element={<Pornhub />} />
                    <Route path="/xvideos" element={<Xvideos />} />
                    <Route path="/fotos-gratis" element={<FotosGratis />} />
                    <Route path="/videos-gratis" element={<VideosGratis />} />
                    <Route path="/tiktoker" element={<Tiktoker />} />
                    <Route path="/modelo" element={<Modelo />} />
                    <Route path="/photography" element={<Photography />} />
                    <Route path="/artista" element={<Artista />} />
                    <Route path="/fashion" element={<Fashion />} />
                    <Route path="/wellness" element={<Wellness />} />
                    <Route
                      path="/digital-creator"
                      element={<DigitalCreator />}
                    />
                    <Route path="/social-media" element={<SocialMedia />} />
                    <Route path="/personalidad" element={<Personalidad />} />
                    <Route path="/beauty-expert" element={<BeautyExpert />} />
                    <Route
                      path="/travel-influencer"
                      element={<TravelInfluencer />}
                    />
                    <Route path="/creative" element={<Creative />} />
                    <Route path="/argentina" element={<Argentina />} />
                    <Route path="/ar/invitacion" element={<InvitacionExclusivaArgentina />} />
                    <Route path="/ar/invite" element={<InvitacionExclusivaArgentina />} />
                    {/* Checkout AR result pages - redirect a Telegram */}
                    <Route path="/ar/invitacion/success" element={<InvitacionArSuccess />} />
                    <Route path="/ar/invitacion/error" element={<InvitacionArError />} />
                    <Route path="/ar/invitacion/pending" element={<InvitacionArPending />} />
                    {/* Flujo VIP: registro forzado → completar perfil → pending → mi-acceso */}
                    <Route path={MEMBERSHIP_ROUTE.REGISTRO} element={<Registro mode="sign-up" />} />
                    <Route path={MEMBERSHIP_ROUTE.ENTRAR} element={<Registro mode="sign-in" />} />
                    <Route path={MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO} element={<CompleteRegistration />} />
                    {/* Alias legacy — la ruta canónica ahora es /portal/completar-perfil */}
                    <Route path="/completar-registro" element={<CompleteRegistration />} />
                    {/* Alias en inglés — Stripe success_url para visitantes EN aterriza acá. */}
                    <Route path={MEMBERSHIP_ROUTE_EN_COMPLETE_PROFILE} element={<CompleteRegistration />} />
                    <Route path={MEMBERSHIP_ROUTE.PENDING_APPROVAL} element={<PendingApproval />} />
                    <Route path={MEMBERSHIP_ROUTE.MI_ACCESO} element={<MiAcceso />} />
                    {/* Free-join: post-perfil → revelar link del grupo free */}
                    <Route path={FREE_JOIN_ROUTE.ES} element={<FreeAccessReady />} />
                    <Route path={FREE_JOIN_ROUTE.EN} element={<FreeAccessReady />} />
                    <Route path={MEMBERSHIP_ACCESS_PORTAL_PATH} element={<MembershipAccessPortal />} />
                    <Route path="/bestfriend-instagram" element={<BestFriendInstagram />} />
                    <Route path="/bestfriend-instagram/success" element={<BestFriendInstagramSuccess />} />
                    {/* BioTemplatePremium — invitación global por país (ARS=MercadoPago / USD=Stripe) */}
                    {/* /invitacion-premium: Mux fullscreen VIP (mismo componente). */}
                    <Route
                      path="/invitacion-premium"
                      element={
                        <Suspense fallback={<div className="fixed inset-0 bg-black" aria-hidden />}>
                          <InvitacionExclusivaMux />
                        </Suspense>
                      }
                    />
                    {/* AR BaseTemplate CTA "Exclusivo" (wishlist community) → canónico.
                        InvitacionExclusivaMux (Mux 1 CTA VIP Stripe). Mismo VIP admin que DualCTA. */}
                    <Route
                      path="/invitacion-exclusive"
                      element={
                        <Suspense fallback={<div className="fixed inset-0 bg-black" aria-hidden />}>
                          <InvitacionExclusivaMux />
                        </Suspense>
                      }
                    />
                    {/* Legacy Spanish slug — same page (bookmarks / campaigns). */}
                    <Route
                      path="/invitacion-exclusiva"
                      element={
                        <Suspense fallback={<div className="fixed inset-0 bg-black" aria-hidden />}>
                          <InvitacionExclusivaMux />
                        </Suspense>
                      }
                    />
                    {/* Video fullscreen Mux con doble CTA (VIP + Telegram gratis), config desde premium-invite */}
                    <Route
                      path="/video-landing"
                      element={
                        <Suspense fallback={<div className="fixed inset-0 bg-black" aria-hidden />}>
                          <DualCTAVideoLanding />
                        </Suspense>
                      }
                    />
                    {/* Video fullscreen con modo configurable vía admin CTA (sin_boton/exclusivo/comunidad/wishlist/video_vip) */}
                    <Route path="/video-vip" element={<VideoVipLanding />} />
                    {/* Landing DualCTA de membresía VIP: video + "Ser Miembro" (Stripe/MP) + canal gratis */}
                    <Route path="/invitacion-miembro" element={<InvitacionMiembro />} />
                    {/*
                      URLs de acceso directo compartibles (no están en el navbar).
                      Sirven para campañas, stories, DMs — cualquier país las puede ver,
                      incluyendo Argentina (AR ve MercadoPago, resto ve Stripe).
                      Agregar o quitar slugs aquí sin tocar el Worker.
                    */}
                    <Route path="/acceso" element={<PremiumInvite />} />
                    <Route path="/unete" element={<PremiumInvite />} />
                    <Route path="/vip" element={<PremiumInvite />} />
                    
                    {/* Membresía VIP exclusiva — ignora la desactivación del plan VIP */}
                    <Route path="/membresia-vip" element={<MembresiaPremiumVip />} />
                    
                    {/* Test pages para desarrolladores — sin restricciones */}
                    <Route path="/test-new-template" element={<TestNewTemplate />} />
                    <Route path="/test-premium-argentina" element={<TestPremiumArgentina />} />
                    <Route path="/test-premium-internacional" element={<TestPremiumInternacional />} />
                    <Route path="/premium-invite" element={<PremiumInvite />} />
                    <Route path="/invitacion-premium/success" element={<PremiumInviteSuccess />} />
                    <Route path="/muchas-gracias" element={<MuchasGracias />} />
                    <Route path="/buenos-aires" element={<BuenosAires />} />
                    <Route path="/deportista" element={<Deportista />} />
                    <Route path="/fitness" element={<Fitness />} />
                    <Route path="/entrenamiento" element={<Entrenamiento />} />
                    <Route path="/atleta" element={<Atleta />} />
                    <Route path="/sobre-mi" element={<Navigate to="/" replace />} />
                    <Route path="/biografia" element={<Biografia />} />
                    <Route path="/contacto" element={<Contacto />} />
                    <Route path="/rutina" element={<Rutina />} />
                    <Route path="/recetas" element={<Recetas />} />
                    <Route path="/skincare" element={<Skincare />} />
                    <Route
                      path="/gracias-por-tu-compra"
                      element={<GraciasPorTuCompra />}
                    />
                    <Route
                      path="/gracias-por-tu-compra-latam"
                      element={<GraciasPorTuCompraLatam />}
                    />
                    {/* Thank you pages: required for Stripe Elements custom → validate purchase & Telegram entry button */}
                    <Route path="/gracias" element={<Gracias />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    {/* Regalo Lucas page */}
                    {/* <Route path="/regalo-lucas" element={<RegaloLucas />} /> oculto mientras se trabaja */}
                    <Route
                      path="/membresia-telegram"
                      element={<MembresiaTelegram />}
                    />
                    <Route
                      path="/invitacion-privada"
                      element={<FullscreenExclusive />}
                    />
                    <Route
                      path="/exclusive"
                      element={<FullscreenExclusive />}
                    />
                    <Route
                      path="/exclusive-ar"
                      element={<FullscreenExclusiveAr />}
                    />
                    <Route
                      path="/exclusive-ar/success"
                      element={<ExclusivoArSuccess />}
                    />
                    <Route
                      path="/sorpresa"
                      element={<FullscreenSorpresa />}
                    />
                    <Route
                      path="/sorprendeme"
                      element={<Sorprendeme />}
                    />
                    <Route
                      path="/vip/:nombre"
                      element={<VipCustomPayment />}
                    />
                    <Route
                      path="/test-exclusivo"
                      element={<TestExclusivo />}
                    />
                    <Route
                      path="/whatsapp-broadcast"
                      element={<WhatsAppBroadcast />}
                    />
                    <Route
                      path="/whatsapp-broadcast/success"
                      element={<WhatsAppBroadcastSuccess />}
                    />
                    <Route
                      path="/terminos-y-condiciones"
                      element={<TerminosYCondiciones />}
                    />
                    {/* Clerk legal consent + CookieBanner — Privacy Policy URL */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/politica-de-privacidad"
                      element={<PrivacyPolicy />}
                    />
                    <Route
                      path="/dar-de-baja"
                      element={<DarDeBaja />}
                    />
                    <Route
                      path="/portfolio"
                      element={<Portfolio />}
                    />
                    <Route
                      path="/mediakit-intro"
                      element={<MediaKitIntro />}
                    />
                    <Route
                      path="/mediakit"
                      element={
                        <MediaKitPasswordGate>
                          <MediaKitDesktop />
                        </MediaKitPasswordGate>
                      }
                    />
                    <Route
                      path="/audiovisual"
                      element={<Audiovisual />}
                    />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <PostHogPageview />
                  <GoogleAnalyticsPageview />
                  <PostHogTrackingVerification />
                  <MetricoolTracker />
                    </I18nLoadingWrapper>
                </CurrentTemplateProvider>
                  </AppClerkProvider>
              </BrowserRouter>
            </LanguageProvider>
          </RegionProvider>
        </TooltipProvider>
      </PostHogProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );

  return appContent;
};

export default App;
